import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useFittingPageSize, useKeepFirstItemVisible } from './useFittingPageSize';

const storedPageKey = (listKey) => `list-page:${listKey}`;

const positivePage = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 1 ? Math.trunc(parsed) : null;
};

const readStoredPage = (listKey) => {
  if (!listKey) return null;
  try {
    return positivePage(sessionStorage.getItem(storedPageKey(listKey)));
  } catch {
    return null;
  }
};

const writeStoredPage = (listKey, page) => {
  if (!listKey) return;
  try {
    sessionStorage.setItem(storedPageKey(listKey), String(page));
  } catch {
    // A blocked or full sessionStorage should not break the list.
  }
};

/** Page that survives leaving the list, from `?page=` first and then sessionStorage. */
const useRememberedPage = (listKey, queryKey) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageState, setPageState] = useState(() => ({
    queryKey,
    page: positivePage(searchParams.get('page')) || readStoredPage(listKey) || 1,
  }));

  if (pageState.queryKey !== queryKey) setPageState({ queryKey, page: 1 });
  const page = pageState.queryKey === queryKey ? pageState.page : 1;
  const setPage = useCallback((next) => {
    setPageState((previous) => ({
      ...previous,
      page: typeof next === 'function' ? next(previous.page) : next,
    }));
  }, []);

  useEffect(() => {
    writeStoredPage(listKey, page);
    const desired = page > 1 ? String(page) : null;
    if ((searchParams.get('page') || null) === desired) return;

    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (desired) next.set('page', desired);
      else next.delete('page');
      return next;
    }, { replace: true });
  }, [page, listKey, searchParams, setSearchParams]);

  return [page, setPage];
};

/**
 * One page of a server-side list, sized to fill the list area.
 *
 * `fetchPage({ page, perPage })` resolves to `{ items, total }`. Any change of
 * `queryKey` (search, filters, sort) starts again from page one; responses that
 * arrive after a newer request was sent are dropped.
 *
 * `listKey` keeps the current page in the address bar and in sessionStorage.
 * Opening a session or an event unmounts the list; coming back reads that page
 * instead of starting again at page one.
 */
export const usePagedList = ({ fetchPage, queryKey, getLayout, fallbackLayout, errorMessage, listKey }) => {
  const listAreaRef = useRef(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useRememberedPage(listKey, queryKey);
  const fetchPageRef = useRef(fetchPage);
  const requestIdRef = useRef(0);
  fetchPageRef.current = fetchPage;

  const perPage = useFittingPageSize({ areaRef: listAreaRef, getLayout, fallbackLayout, revision: loading });
  useKeepFirstItemVisible(perPage, setPage);

  useEffect(() => {
    if (!perPage) return;
    const requestId = ++requestIdRef.current;
    const isLatest = () => requestId === requestIdRef.current;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const result = await fetchPageRef.current({ page, perPage });
        if (!isLatest()) return;
        setItems(result.items);
        setTotal(Number.isFinite(result.total) ? result.total : result.items.length);
      } catch (err) {
        if (!isLatest()) return;
        console.error(errorMessage, err);
        setError(errorMessage);
        setItems([]);
        setTotal(0);
      } finally {
        if (isLatest()) setLoading(false);
      }
    })();
  }, [page, perPage, queryKey, errorMessage]);

  const totalPages = Math.max(1, Math.ceil(total / (perPage || 1)));
  const currentPage = Math.min(page, totalPages);

  useEffect(() => {
    if (!perPage || total <= 0 || page <= totalPages) return;
    setPage(totalPages);
  }, [page, perPage, total, totalPages, setPage]);

  const goToPage = (nextPage) => {
    const parsed = Number(nextPage);
    if (!Number.isFinite(parsed)) return;
    setPage(Math.min(Math.max(Math.trunc(parsed), 1), totalPages));
  };

  return {
    listAreaRef,
    items,
    loading,
    error,
    pagination: {
      currentPage,
      totalPages,
      total,
      rangeStart: total === 0 ? 0 : (currentPage - 1) * perPage + 1,
      rangeEnd: Math.min(currentPage * perPage, total),
      goToPage,
    },
  };
};
